<script setup lang="ts">
import type { LocationRecord } from '~/types/datocms'
import LocationListItem from './LocationListItem.vue'

interface LocationListProps {
  data: LocationRecord[]
  isLoading: boolean
  noRecordsMessage?: string
}

const props = withDefaults(defineProps<LocationListProps>(), {
  noRecordsMessage: 'No records to display'
})

const flattenedItems = computed(() =>
  props.data.flatMap((record) =>
    record.imageGallery.map((image) => ({
      data: record,
      image
    }))
  )
)

const chunkedData = computed(() => {
  const chunkSize = 11
  const items = flattenedItems.value
  return Array.from({ length: Math.ceil(items.length / chunkSize) }, (_, i) =>
    items.slice(i * chunkSize, i * chunkSize + chunkSize)
  )
})
</script>

<template>
  <Transition
    mode="out-in"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
    class="relative pb-8 lg:py-12"
    enter-active-class="transition-opacity duration-300"
    leave-active-class="transition-opacity duration-300"
  >
    <div v-if="isLoading" class="py-10 text-center">
      <Spinner class="mx-auto mt-10" />
    </div>
    <div v-else-if="!data.length" class="text-bold py-10 text-center font-bold uppercase">
      {{ noRecordsMessage }}
    </div>
    <TransitionGroup
      v-else
      appear
      tag="div"
      class="space-y-10"
      enter-from-class="opacity-0"
      enter-active-class="transition-transform duration-300"
    >
      <div v-for="(chunk, index) in chunkedData" :key="index" class="location-list-grid">
        <LocationListItem
          v-for="(item, i) in chunk"
          :key="`${item.data.id}-${i}`"
          :data="item.data"
          :image="item.image"
        />
      </div>
    </TransitionGroup>
  </Transition>
</template>

<style lang="postcss">
.location-list-grid {
  @apply grid w-full grid-cols-12 gap-2 lg:gap-4;

  > div {
    @apply col-span-12;
  }
  @screen md {
    grid-template-areas:
      'a a a a b b b b c c c .'
      'a a a a d d d d . . . .'
      '. . . . . e e e e e e e'
      '. . f f f g g g g h h h '
      'i i i i j j j j k k k k';

    width: 100%;

    > *:nth-child(11n + 1) {
      grid-area: a;

      img {
        @apply aspect-[7/9];
      }
    }

    > *:nth-child(11n + 2) {
      grid-area: b;

      img {
        @apply aspect-[16/10];
      }
    }

    > *:nth-child(11n + 3) {
      grid-area: c;

      img {
        @apply aspect-[16/10];
      }
    }

    > *:nth-child(11n + 4) {
      grid-area: d;

      img {
        @apply aspect-[16/10];
      }
    }

    > *:nth-child(11n + 5) {
      grid-area: e;

      img {
        @apply aspect-[16/10];
      }
    }

    > *:nth-child(11n + 6) {
      grid-area: f;

      img {
        @apply aspect-[7/9];
      }
    }

    > *:nth-child(11n + 7) {
      grid-area: g;

      img {
        @apply aspect-[7/9];
      }
    }

    > *:nth-child(11n + 8) {
      grid-area: h;

      img {
        @apply aspect-[16/10];
      }
    }

    > *:nth-child(11n + 9) {
      grid-area: i;

      img {
        @apply aspect-[7/9];
      }
    }

    > *:nth-child(11n + 10) {
      grid-area: j;

      img {
        @apply aspect-[7/9];
      }
    }

    > *:nth-child(11n + 11) {
      grid-area: k;

      img {
        @apply aspect-[16/10];
      }
    }
  }
}
</style>
